import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type ListBody, type S } from "@/shared/api/client";
import { localName } from "@/shared/i18n";
import { orgOption } from "@/shared/ui/EntityPicker";

export type PO = S["InventoryPurchaseOrder"];
export type Bill = S["InventoryBill"];

export const productOption = (p: S["Product"]) => ({ id: p.id, label: localName(p), sub: p.sku || undefined });
export const supplierOption = (o: S["Organization"]) => orgOption(o);

export function useWarehouses(enabled = true) {
  return useQuery({ queryKey: ["/v1/inventory/warehouses", "all"], queryFn: () => api.get<ListBody<S["InventoryWarehouse"]>>("/v1/inventory/warehouses", { limit: 100 }), staleTime: 60_000, enabled });
}

/** Whether stock is kept in more than one place; until then, warehouse choices and transfers stay out of the way. */
export function useManyWarehouses(enabled = true): boolean {
  const wh = useWarehouses(enabled);
  return (wh.data?.items ?? []).filter((w) => w.is_active).length > 1;
}

/** The warehouse to use while there's no choice to make: the first active one. */
export const firstWarehouse = (items?: S["InventoryWarehouse"][]) => items?.find((w) => w.is_active)?.id ?? "";

export function WarehouseSelect({ value, onChange, allowAll }: { value: string; onChange: (v: string) => void; allowAll?: boolean }) {
  const { t } = useTranslation();
  const wh = useWarehouses();
  return (
    <select className="field w-auto" value={value} onChange={(e) => onChange(e.target.value)} aria-label={t("invt.warehouse")}>
      {allowAll && <option value="">{t("common.all")}</option>}
      {(wh.data?.items ?? []).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
    </select>
  );
}

export interface Tracked { batch_code: string; expiry_date: string; serials: string }

/**
 * A stock line's batch, expiry date and serial numbers: only those its product is
 * tracked by (set in the product's inventory settings), so most lines show none.
 */
export function TrackingInputs({ productId, value, onChange, serials = true, expiry = true }: {
  productId: string | null | undefined; value: Tracked; onChange: (p: Partial<Tracked>) => void; serials?: boolean; expiry?: boolean;
}) {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ["/v1/inventory/products", productId, "settings"], enabled: !!productId, staleTime: 60_000,
    queryFn: () => api.get<S["InventoryProductSettings"]>(`/v1/inventory/products/${productId}/settings`) });
  const s = q.data;
  if (s?.tracking === "batch") {
    return (
      <div className="flex gap-2 sm:col-span-full">
        <input className="field num flex-1" dir="ltr" placeholder={t("invt.batch")} aria-label={t("invt.batch")} value={value.batch_code} onChange={(e) => onChange({ batch_code: e.target.value })} />
        {s.has_expiry && expiry && <input className="field flex-1" type="date" aria-label={t("invt.expiry")} value={value.expiry_date} onChange={(e) => onChange({ expiry_date: e.target.value })} />}
      </div>
    );
  }
  if (s?.tracking === "serial" && serials) {
    return (
      <textarea className="field num sm:col-span-full" dir="ltr" rows={1} placeholder={`${t("invt.serials")} — ${t("invt.serialsHint")}`} aria-label={t("invt.serials")}
        value={value.serials} onChange={(e) => onChange({ serials: e.target.value })} />
    );
  }
  return null;
}
